import { LIMIT_BY_QUANTITY_FILTERS, LIMIT_BY_QUANTITY_SCOPES, LIMIT_BY_QUANTITY_TYPES } from 'src/models/discount/Discount'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'

export function validateQuantityLimits ({
  redemption,
  orderItem,
  nonDiscountedItems,
  orderItemMatches,
  unpaidOrderItems
}) {
  const isVoucher = redemption.typeName === 'Voucher'
  if (isVoucher) {
    redemption = redemption.voucherType
  }

  if (!redemption.limitByQuantityEnabled) return true

  // Skip if item explicitly excludes this redemption
  if (redemption.typeName === 'Discount' && _.includes(orderItem.explicitDiscountRemovalIds, redemption.id)) return

  if (!shouldApplyTicketRedemption(redemption, orderItemMatches)) return false
  if (!shouldApplyActivityTicketRedemption(redemption, orderItemMatches, orderItem)) return false

  if (!processAllItemsRedemption({
    redemption,
    orderItem,
    unpaidOrderItems,
    nonDiscountedItems
  })) return false

  if (!processLastItemRedemption({
    redemption,
    orderItem,
    orderItemMatches,
    nonDiscountedItems
  })) return false

  return true
}

// main functions
function shouldApplyTicketRedemption (redemption, orderItemMatches) {
  if (!isTicketRedemptionWithQuantityFilter(redemption)) {
    return true // or whatever your default behavior should be
  }

  const distinctCount = countDistinctTickets(orderItemMatches, redemption.limitByQuantityFilter)
  return distinctCount >= redemption.numberOfMatchingItemsRequired
}

function shouldApplyActivityTicketRedemption (redemption, orderItemMatches, orderItem) {
  if (!isActivityTicketRedemption(redemption)) return true

  if (usesActivitySessionOrActivityFilter(redemption)) {
    const count = countDistinctActivities(orderItemMatches, redemption.limitByQuantityFilter)
    return count >= redemption.numberOfMatchingItemsRequired
  }

  if (redemption.limitByQuantityFilter === LIMIT_BY_QUANTITY_FILTERS.ACTIVITY_TICKET) {
    const count = countDistinctActivityTickets(
      orderItemMatches,
      orderItem,
      redemption.limitByQuantityScope
    )
    return count >= redemption.numberOfMatchingItemsRequired
  }

  return false // Unsupported filter type
}

function processAllItemsRedemption ({
  redemption,
  orderItem,
  unpaidOrderItems,
  nonDiscountedItems
}) {
  if (!isAllItemsRedemption(redemption)) return true

  // Case 1: Already part of a matching redemption group
  if (hasMatchingRedemptionGroup(orderItem, unpaidOrderItems)) {
    return true
  }

  // Case 2: Not enough matching items
  if (nonDiscountedItems.length < redemption.numberOfMatchingItemsRequired) {
    return false
  }

  // Case 3: Create new redemption group
  const groupKey = generateGroupKey()
  assignGroupKeyToItems(nonDiscountedItems, groupKey)
  return true
}

function processLastItemRedemption ({
  redemption,
  orderItem,
  orderItemMatches,
  nonDiscountedItems
}) {
  if (!isLastItemRedemption(redemption)) return true

  if (hasRedemptionedGroupMember(orderItem, orderItemMatches)) {
    return false
  }

  const matchLength = countGroupMembers(orderItemMatches, orderItem.discountGroupKey)
  const requiredCount = redemption.numberOfMatchingItemsRequired
  if (orderItem.discountGroupKey) {
    if (matchLength === requiredCount) {
      return isLastInGroup(orderItem, orderItemMatches)
    } else if (matchLength < requiredCount &&
             nonDiscountedItems.length + matchLength >= requiredCount) {
      replenishGroup(orderItem.discountGroupKey, nonDiscountedItems, requiredCount - matchLength)
      return true
    }
    return false
  } else if (nonDiscountedItems.length >= requiredCount) {
    createNewGroup(orderItem, nonDiscountedItems, requiredCount)
    return true
  }

  return false
}

// ticket helper functions
function isTicketRedemptionWithQuantityFilter (redemption) {
  return (
    redemption.type === ORDER_ITEM_TYPES.TICKET &&
    [LIMIT_BY_QUANTITY_FILTERS.SHOWING, LIMIT_BY_QUANTITY_FILTERS.MOVIE].includes(redemption.limitByQuantityFilter)
  )
}

function getTicketIdentifier (orderItem, filterType) {
  if (filterType === LIMIT_BY_QUANTITY_FILTERS.SHOWING) {
    return orderItem?.ticket?.showing?.id
  }
  return orderItem?.ticket?.showing?.movie?.id
}

function countDistinctTickets (orderItemMatches, filterType) {
  const identifiers = orderItemMatches.map(item => getTicketIdentifier(item, filterType))
  const uniqueIdentifiers = _.uniq(_.compact(identifiers))
  return uniqueIdentifiers.length
}

// activity ticket helper functions
function isActivityTicketRedemption (redemption) {
  return redemption.type === ORDER_ITEM_TYPES.ACTIVITY_TICKET
}

function usesActivitySessionOrActivityFilter (redemption) {
  return [LIMIT_BY_QUANTITY_FILTERS.ACTIVITY_SESSION,
    LIMIT_BY_QUANTITY_FILTERS.ACTIVITY].includes(redemption.limitByQuantityFilter)
}

function getActivityIdentifier (orderItem, filterType) {
  switch (filterType) {
    case LIMIT_BY_QUANTITY_FILTERS.ACTIVITY_SESSION:
      return orderItem?.activityTicket?.activitySession?.id
    case LIMIT_BY_QUANTITY_FILTERS.ACTIVITY:
      return orderItem?.activityTicket?.activitySession?.activity?.id
    default:
      return null
  }
}

function countDistinctActivities (orderItemMatches, filterType) {
  const identifiers = orderItemMatches.map(oi => getActivityIdentifier(oi, filterType))
  return _.uniq(_.compact(identifiers)).length
}

function filterByActivityScope (orderItemMatches, orderItem, scope) {
  return orderItemMatches.filter(oi => {
    if (scope === null) return true

    const current = orderItem?.activityTicket
    const compare = oi?.activityTicket

    switch (scope) {
      case LIMIT_BY_QUANTITY_SCOPES.ACTIVITY_SESSION:
        return compare?.activitySession?.id === current?.activitySession?.id
      case LIMIT_BY_QUANTITY_SCOPES.ACTIVITY:
        return compare?.activitySession?.activity?.id === current?.activitySession?.activity?.id
      default:
        return false
    }
  })
}

function countDistinctActivityTickets (orderItemMatches, orderItem, scope) {
  const filtered = filterByActivityScope(orderItemMatches, orderItem, scope)
  const ticketIds = filtered.map(oi => oi.activityTicket?.id)
  return _.uniq(_.compact(ticketIds)).length
}

// all items helper functions
function isAllItemsRedemption (redemption) {
  return redemption.limitByQuantityType === LIMIT_BY_QUANTITY_TYPES.ALL_ITEMS.value
}

function hasMatchingRedemptionGroup (orderItem, unpaidOrderItems) {
  return orderItem.discountGroupKey &&
         _.find(unpaidOrderItems, oi =>
           oi !== orderItem &&
           oi.discountGroupKey === orderItem.discountGroupKey &&
           oi.discountId
         )
}

function generateGroupKey () {
  return crypto.getRandomValues(new Uint32Array(1))[0]
}

function assignGroupKeyToItems (items, groupKey) {
  _.each(items, item => { item.discountGroupKey = groupKey })
}

// last item helper functions
function isLastItemRedemption (redemption) {
  return redemption.limitByQuantityType === LIMIT_BY_QUANTITY_TYPES.LAST_ITEM.value
}

function hasRedemptionedGroupMember (orderItem, orderItemMatches) {
  return orderItem.discountGroupKey &&
         _.some(orderItemMatches, oi =>
           oi.discountGroupKey === orderItem.discountGroupKey &&
           (oi.discount || oi.voucher)
         )
}

function countGroupMembers (orderItemMatches, groupKey) {
  return _.filter(orderItemMatches, oi => oi.discountGroupKey === groupKey).length
}

function isLastInGroup (orderItem, orderItemMatches) {
  return _.findLast(orderItemMatches, oi => oi.discountGroupKey === orderItem.discountGroupKey) === orderItem
}

function replenishGroup (groupKey, nonDiscountedItems, neededCount) {
  const itemsToAdd = nonDiscountedItems.splice(0, neededCount)
  _.each(itemsToAdd, item => { item.discountGroupKey = groupKey })
}

function createNewGroup (orderItem, nonDiscountedItems, requiredCount) {
  const groupKey = crypto.getRandomValues(new Uint32Array(1))[0]
  orderItem.discountGroupKey = groupKey

  const otherItems = _.filter(nonDiscountedItems, ndi => ndi !== orderItem)
  const itemsToAdd = otherItems.slice(0, requiredCount - 1)
  _.each(itemsToAdd, oi => { oi.discountGroupKey = groupKey })
}
